<script>
	import { onMount } from "svelte";
	import { Link } from "svelte-routing";
	import Header from "../components/Header.svelte";

	onMount(() => {
		window.scrollTo(0, 0);
	});
</script>

<svelte:head>
	<title>Boanalys för Banker – AI-driven kreditbedömning av BRF:er</title>
	<meta
		name="description"
		content="Boanalys erbjuder banker AI-driven riskbedömning av bostadsrättsföreningar. Effektivisera kreditprocessen, identifiera risker tidigt och ta bättre beslut med datadriven BRF-analys."
	/>
</svelte:head>

<main>
	<Header />

	<!-- HERO -->
	<section class="hero">
		<div class="animated-background">
			<div class="shape shape-1"></div>
			<div class="shape shape-2"></div>
			<div class="shape shape-3"></div>
			<div class="shape shape-4"></div>
		</div>
		<div class="hero-content">
			<div class="eyebrow">Boanalys för Banker</div>
			<h1>AI-driven riskbedömning<br />av bostadsrättsföreningar</h1>
			<p class="sub-headline">
				Effektivisera kreditprocessen, identifiera risker tidigt och fatta
				säkrare beslut – med automatiserad analys av 30 000+ BRF:er.
			</p>
			<div class="hero-ctas">
				<a href="mailto:info@boanalys.io?subject=Bankpaket - intresseanmälan">
					<button class="action-button">Kontakta oss</button>
				</a>
				<a href="#features">
					<button class="secondary-button">Se vad som ingår</button>
				</a>
			</div>
			<div class="trust-badges">
				<span class="trust-badge">✓ Marknadens mest pålitliga siffror</span>
				<span class="trust-badge">✓ GDPR-kompatibel</span>
				<span class="trust-badge">✓ API-integration</span>
			</div>
		</div>
	</section>

	<!-- PROBLEMET -->
	<section class="problem-section">
		<div class="problem-content">
			<h2>Kreditgivning till BRF-köpare är komplex</h2>
			<p class="section-intro">
				Banker beviljar miljarder i bolån varje år kopplade till bostadsrätter.
				Men bedömningen av föreningens ekonomiska hälsa är ofta manuell,
				tidskrävande och inkonsekvent.
			</p>
			<div class="problem-cards">
				<div class="problem-card">
					<div class="problem-icon"><i class="fas fa-clock"></i></div>
					<h3>Tidskrävande manuell granskning</h3>
					<p>
						Kredithandläggare lägger timmar på att läsa årsredovisningar och
						beräkna nyckeltal för hand – en process som är sårbar för
						mänskliga fel.
					</p>
				</div>
				<div class="problem-card">
					<div class="problem-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3>Dolda risker missas</h3>
					<p>
						Eftersatta underhållsplaner, höga skuldsättningar och svaga
						likviditetsnivåer kan vara svåra att identifiera utan djupgående
						analys av historisk data.
					</p>
				</div>
				<div class="problem-card">
					<div class="problem-icon"><i class="fas fa-balance-scale"></i></div>
					<h3>Inkonsekvent bedömning</h3>
					<p>
						Utan ett standardiserat verktyg varierar riskbedömningen mellan
						handläggare, kontor och regioner – vilket skapar ojämn
						kreditkvalitet i portföljen.
					</p>
				</div>
			</div>
		</div>
	</section>

	<!-- DATAKVALITET -->
	<section class="quality-section">
		<div class="quality-content">
			<div class="quality-text">
				<div class="quality-badge">Datakvalitet</div>
				<h2>Marknaden har ett dataproblem. Vi löste det.</h2>
				<p>
					Många aktörer på marknaden presenterar BRF-data med hög
					konfidensgrad – men vi har granskat siffrorna och hittat
					systematiska fel, inte minst i bolån per kvadratmeter. Fel som
					direkt påverkar en banks kreditbedömning.
				</p>
				<p>
					Boanalys läser <strong>varje årsredovisning direkt</strong> – den
					officiella källan – med hjälp av AI som är tränad på att extrahera
					finansiell data med hög precision. 
				</p>
				<div class="quality-caveat">
					<i class="fas fa-info-circle"></i>
					<span>
						Data uppdateras när en ny årsredovisning analyseras, vilket
						sker ungefär en gång per år per förening.
					</span>
				</div>
			</div>
			<div class="quality-points">
				<div class="quality-point">
					<div class="qp-icon"><i class="fas fa-file-alt"></i></div>
					<div>
						<h4>Direkt från årsredovisningen</h4>
						<p>Vi läser källdokumentet – inte en aggregerad databas med okänt ursprung.</p>
					</div>
				</div>
				<div class="quality-point">
					<div class="qp-icon"><i class="fas fa-robot"></i></div>
					<div>
						<h4>AI-extraktion med hög precision</h4>
						<p>Vår modell är optimerad för svenska BRF-årsredovisningar och validerar utplockat data.</p>
					</div>
				</div>
				<div class="quality-point">
					<div class="qp-icon"><i class="fas fa-history"></i></div>
					<div>
						<h4>Historiska trender per förening</h4>
						<p>Se hur ekonomin utvecklats år för år – inte bara ett ögonblicksvärde.</p>
					</div>
				</div>
				<div class="quality-point">
					<div class="qp-icon"><i class="fas fa-check-double"></i></div>
					<div>
						<h4>Transparent om begränsningar</h4>
						<p>Vi anger alltid när data är hämtad och från vilken årsredovisning – inga dolda uppdateringsdatum.</p>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- FEATURES -->
	<section class="features-section" id="features">
		<div class="features-content">
			<h2>Vad ingår i Bankpaketet?</h2>
			<p class="section-intro">
				En komplett verktygslåda för banker som vill ta datadrivna
				kreditbeslut kopplade till bostadsrättsföreningar.
			</p>

			<div class="features-grid">
				<div class="feature-card">
					<div class="feature-icon-wrap">
						<i class="fas fa-database"></i>
					</div>
					<div class="feature-text">
						<h3>100+ strukturerade nyckeltal per förening</h3>
						<p>
							Skuld per kvm, likviditet, ränteexponering, avgiftsutveckling –
							extraherade direkt ur årsredovisningen och presenterade som
							tydliga, maskinläsbara datapunkter. Inga tolkningar, bara siffror.
						</p>
					</div>
				</div>

				<div class="feature-card">
					<div class="feature-icon-wrap">
						<i class="fas fa-file-invoice-dollar"></i>
					</div>
					<div class="feature-text">
						<h3>Automatiserad analys av årsredovisningar</h3>
						<p>
							Vår AI extraherar och strukturerar data från årsredovisningar
							på sekunder. Lånevolym, ränteexponering, renoveringshistorik
							och 100+ nyckeltal – utan manuellt arbete.
						</p>
					</div>
				</div>

				<div class="feature-card">
					<div class="feature-icon-wrap">
						<i class="fas fa-layer-group"></i>
					</div>
					<div class="feature-text">
						<h3>Portföljöverblick & bulk-analys</h3>
						<p>
							Analysera hela er BRF-portfölj på en gång. Identifiera vilka
							föreningar som har försämrad ekonomi, lån som löper ut eller
							planerade stora renoveringar – innan det syns i betalningarna.
						</p>
					</div>
				</div>

				<div class="feature-card">
					<div class="feature-icon-wrap">
						<i class="fas fa-code"></i>
					</div>
					<div class="feature-text">
						<h3>API-integration</h3>
						<p>
							Integrera Boanalys direkt i ert befintliga kredithandläggningssystem
							via REST API. Hämta strukturerade nyckeltal automatiskt när en
							låneansökan kommer in – och slipp manuell sökning.
						</p>
					</div>
				</div>

				<div class="feature-card">
					<div class="feature-icon-wrap">
						<i class="fas fa-bell"></i>
					</div>
					<div class="feature-text">
						<h3>Automatiska varningar</h3>
						<p>
							Bevaka föreningar i er portfölj och få notiser när
							nya årsredovisningar publiceras, avgifter höjs eller
							ekonomiska nyckeltal försämras markant.
						</p>
					</div>
				</div>

				<div class="feature-card">
					<div class="feature-icon-wrap">
						<i class="fas fa-comments"></i>
					</div>
					<div class="feature-text">
						<h3>BoBot – AI-assistent för handläggare</h3>
						<p>
							Låt kredithandläggare ställa direkta frågor om en förening:
							"Hur ser lånstrukturen ut?", "Är underhållsplanen
							finansierad?" – och få svar på sekunder.
						</p>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- HUR DET FUNGERAR -->
	<section class="how-section">
		<div class="how-content">
			<h2>Så enkelt fungerar det</h2>
			<div class="steps">
				<div class="step">
					<div class="step-number">1</div>
					<h3>Sök eller ladda upp</h3>
					<p>
						Sök en BRF via organisationsnummer eller namn, eller ladda upp
						årsredovisningen direkt. Vi har redan analyserat 30 000+
						föreningar.
					</p>
				</div>
				<div class="step-arrow"><i class="fas fa-arrow-right"></i></div>
				<div class="step">
					<div class="step-number">2</div>
					<h3>Få strukturerad data direkt</h3>
					<p>
						Inom sekunder får ni ekonomisk sammanfattning, låneprofil,
						100+ nyckeltal och flaggor för kritiska riskfaktorer.
					</p>
				</div>
				<div class="step-arrow"><i class="fas fa-arrow-right"></i></div>
				<div class="step">
					<div class="step-number">3</div>
					<h3>Ta beslut med trygghet</h3>
					<p>
						Använd analysen som underlag i kreditbeslut, dokumentera
						bedömningen och följ upp föreningen löpande via bevakningsfunktionen.
					</p>
				</div>
			</div>
		</div>
	</section>

	<!-- NYCKELTAL -->
	<section class="metrics-section">
		<div class="metrics-content">
			<h2>Vad analyserar vi?</h2>
			<p class="section-intro">
				Boanalys extraherar och beräknar över 100 nyckeltal per förening.
				Här är de viktigaste ur ett kreditperspektiv.
			</p>
			<div class="metrics-grid">
				<div class="metric-group">
					<div class="metric-group-title">
						<i class="fas fa-coins"></i> Skuld & Finansiering
					</div>
					<ul>
						<li>Lån per kvm och per lägenhet</li>
						<li>Ränteexponering och bindningstider</li>
						<li>Låneförfall de kommande 1–5 åren</li>
						<li>Räntekänslighetsanalys</li>
					</ul>
				</div>
				<div class="metric-group">
					<div class="metric-group-title">
						<i class="fas fa-chart-bar"></i> Ekonomisk hälsa
					</div>
					<ul>
						<li>Resultat- och balansräkningsanalys</li>
						<li>Likviditet och kassaflöde</li>
						<li>Avgiftsutveckling historiskt</li>
						<li>Soliditet och eget kapital</li>
					</ul>
				</div>
				<div class="metric-group">
					<div class="metric-group-title">
						<i class="fas fa-tools"></i> Underhåll & Framtid
					</div>
					<ul>
						<li>Underhållsplanens finansieringsgrad</li>
						<li>Planerade stora renoveringar</li>
						<li>Fastighetens ålder och skick</li>
						<li>Historiska underhållskostnader</li>
					</ul>
				</div>
				<div class="metric-group">
					<div class="metric-group-title">
						<i class="fas fa-leaf"></i> Energi & Hållbarhet
					</div>
					<ul>
						<li>Energideklarationens klass (A–G)</li>
						<li>Solceller och värmepump</li>
						<li>CO2-utsläpp och energieffektivitet</li>
						<li>Potentiella energiinvesteringar</li>
					</ul>
				</div>
			</div>
		</div>
	</section>

	<!-- USE CASES -->
	<section class="usecases-section">
		<div class="usecases-content">
			<h2>Användningsområden</h2>
			<div class="usecases">
				<div class="usecase">
					<div class="usecase-icon"><i class="fas fa-user-tie"></i></div>
					<h3>Kredithandläggare</h3>
					<p>
						Hämta upp en BRF-analys direkt i kreditärendet. Spara tid,
						dokumentera riskbedömning och säkerställ att inget kritiskt
						missas.
					</p>
				</div>
				<div class="usecase">
					<div class="usecase-icon"><i class="fas fa-chart-pie"></i></div>
					<h3>Riskavdelning</h3>
					<p>
						Övervaka hela portföljen av BRF-kopplade lån. Identifiera
						koncentrationer av risk och föreningar med försämrad ekonomi.
					</p>
				</div>
				<div class="usecase">
					<div class="usecase-icon"><i class="fas fa-university"></i></div>
					<h3>Compliance & Revision</h3>
					<p>
						Standardisera och dokumentera kreditbedömningar av BRF:er.
						Förenkla intern och extern granskning med tydliga
						dataunderlag.
					</p>
				</div>
			</div>
		</div>
	</section>

	<!-- PRISSÄTTNING -->
	<section class="pricing-section">
		<div class="pricing-content">
			<h2>Marknadens lägsta pris – utan avkall på kvalitet</h2>
			<p class="section-intro">
				Vi är ett ungt bolag med ett tydligt mål: att bli den självklara
				BRF-datapartnern för svenska företag. Därför erbjuder vi ett
				konkurrenskraftigt pris för tidiga kunder.
			</p>
			<div class="pricing-card">
				<div class="pricing-card-inner">
					<div class="pricing-label">Bankpaket</div>
					<div class="pricing-price">Kontakta oss för pris</div>
					<p class="pricing-tagline">
						Vi skräddarsyr ett upplägg efter er volym, integration och behov.
						Oavsett om ni är en nischbank eller ett stort kreditinstitut.
					</p>
					<ul class="pricing-list">
						<li><i class="fas fa-check"></i> Tillgång till 30 000+ BRF:er</li>
						<li><i class="fas fa-check"></i> 100+ nyckeltal per förening</li>
						<li><i class="fas fa-check"></i> BoBot AI-assistent för handläggare</li>
						<li><i class="fas fa-check"></i> Portföljövervakning & bulk-analys</li>
						<li><i class="fas fa-check"></i> REST API (valfritt tillägg)</li>
						<li><i class="fas fa-check"></i> Onboarding & support inkluderat</li>
					</ul>
					<a href="mailto:info@boanalys.io?subject=Bankpaket - intresseanmälan">
						<button class="action-button pricing-cta">
							Skicka intresseanmälan
						</button>
					</a>
				</div>
				<div class="pricing-note">
					<i class="fas fa-handshake"></i>
					Vi letar efter vår första bankpartner och är beredda att gå långt för
					att hitta ett upplägg som fungerar för er. Hör av er så hittar vi en lösning.
				</div>
			</div>
		</div>
	</section>

	<!-- CTA -->
	<section class="cta-section">
		<div class="cta-content">
			<h2>Hör av er – vi berättar mer</h2>
			<p>
				Skicka en intresseanmälan så hör vi av oss för att boka ett
				kort samtal. Inga säljare, inga långa processer. Bara ett ärligt
				samtal om hur vi kan hjälpa er bank.
			</p>
			<a href="mailto:info@boanalys.io?subject=Bankpaket - intresseanmälan">
				<button class="action-button cta-primary">
					<i class="fas fa-envelope"></i> Skicka intresseanmälan
				</button>
			</a>
			<p class="cta-note">
				Vi svarar normalt inom 1 arbetsdag.
			</p>
		</div>
	</section>
</main>

<style>
	/* ─── Hero ─────────────────────────────────────── */
	.hero {
		position: relative;
		color: #333;
		text-align: center;
		padding: 120px 20px 140px;
		margin-top: -20px;
	}

	.animated-background {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		z-index: 1;
		filter: blur(100px);
	}

	.shape {
		position: absolute;
		border-radius: 50%;
		opacity: 0.65;
	}

	/* Bank-toned blue/teal palette */
	.shape-1 {
		width: 420px;
		height: 420px;
		background-color: #a3c4f5;
		top: 5%;
		left: 15%;
		animation: move1 11s infinite alternate ease-in-out;
	}
	.shape-2 {
		width: 500px;
		height: 500px;
		background-color: #b0d4e3;
		top: 35%;
		left: 55%;
		animation: move2 13s infinite alternate ease-in-out;
	}
	.shape-3 {
		width: 360px;
		height: 360px;
		background-color: #c9e8d5;
		bottom: 5%;
		right: 15%;
		animation: move3 12s infinite alternate ease-in-out;
	}
	.shape-4 {
		width: 470px;
		height: 470px;
		background-color: #d0c9f0;
		top: 55%;
		left: 5%;
		animation: move4 15s infinite alternate ease-in-out;
	}

	@keyframes move1 {
		from { transform: translate(0, 0) rotate(0deg); }
		to   { transform: translate(90px, 40px) rotate(25deg); }
	}
	@keyframes move2 {
		from { transform: translate(0, 0) scale(1); }
		to   { transform: translate(-70px, 55px) scale(1.15); }
	}
	@keyframes move3 {
		from { transform: translate(0, 0); }
		to   { transform: translate(-45px, -90px); }
	}
	@keyframes move4 {
		from { transform: translate(0, 0) rotate(0deg) scale(1); }
		to   { transform: translate(110px, -70px) rotate(-40deg) scale(1.1); }
	}

	.hero-content {
		position: relative;
		z-index: 5;
		max-width: 860px;
		margin: 0 auto;
	}

	.eyebrow {
		display: inline-block;
		background: rgba(255, 255, 255, 0.8);
		border: 1px solid rgba(0, 0, 0, 0.12);
		padding: 6px 18px;
		border-radius: 20px;
		font-size: 0.85rem;
		font-weight: 600;
		letter-spacing: 0.05em;
		text-transform: uppercase;
		color: #444;
		margin-bottom: 24px;
	}

	.hero-content h1 {
		font-size: 3rem;
		line-height: 1.15;
		margin-bottom: 24px;
	}

	.sub-headline {
		font-size: 1.2rem;
		max-width: 680px;
		margin: 0 auto 30px;
		opacity: 0.85;
		line-height: 1.6;
	}

	.hero-ctas {
		display: flex;
		gap: 16px;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 30px;
	}

	.trust-badges {
		display: flex;
		justify-content: center;
		gap: 16px;
		flex-wrap: wrap;
	}

	.trust-badge {
		font-size: 0.9rem;
		color: #1a3d6e;
		font-weight: 500;
		background: rgba(255, 255, 255, 0.85);
		padding: 7px 16px;
		border-radius: 20px;
		border: 1px solid rgba(26, 61, 110, 0.2);
	}

	/* ─── Buttons ───────────────────────────────────── */
	.action-button {
		background-color: #343a40;
		color: white;
		padding: 13px 28px;
		border: none;
		border-radius: 8px;
		font-weight: 600;
		font-size: 1rem;
		cursor: pointer;
		transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s;
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
	}

	.action-button:hover {
		background-color: #495057;
		transform: translateY(-2px);
		box-shadow: 0 5px 12px rgba(0, 0, 0, 0.16);
	}

	.cta-primary {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 15px 34px;
		font-size: 1.05rem;
	}

	.secondary-button {
		background: transparent;
		border: 2px solid #343a40;
		color: #343a40;
		padding: 13px 28px;
		border-radius: 8px;
		font-weight: 600;
		font-size: 1rem;
		cursor: pointer;
		transition: background-color 0.2s, color 0.2s, transform 0.1s;
		display: inline-flex;
		align-items: center;
		gap: 8px;
	}

	.secondary-button:hover {
		background-color: #343a40;
		color: white;
		transform: translateY(-2px);
	}

	/* ─── Shared section styles ──────────────────────── */
	.section-intro {
		font-size: 1.1rem;
		max-width: 680px;
		margin: 0 auto 50px;
		line-height: 1.7;
		color: #555;
		text-align: center;
	}

	/* ─── Problem section ────────────────────────────── */
	.problem-section {
		padding: 90px 20px;
	}

	.problem-content {
		max-width: 1100px;
		margin: 0 auto;
		text-align: center;
	}

	.problem-content h2 {
		font-size: 2.4rem;
		margin-bottom: 20px;
	}

	.problem-cards {
		display: flex;
		gap: 30px;
		flex-wrap: wrap;
		justify-content: center;
	}

	.problem-card {
		flex: 1;
		min-width: 260px;
		max-width: 320px;
		background: white;
		border-radius: 12px;
		padding: 34px 28px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
		text-align: left;
	}

	.problem-icon {
		width: 52px;
		height: 52px;
		border-radius: 50%;
		background: #fff0ed;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.3rem;
		color: var(--hover);
		margin-bottom: 18px;
	}

	.problem-card h3 {
		font-size: 1.1rem;
		margin-bottom: 10px;
		color: #222;
	}

	.problem-card p {
		font-size: 0.95rem;
		line-height: 1.65;
		color: #666;
	}

	/* ─── Features section ───────────────────────────── */
	.features-section {
		padding: 90px 20px;
	}

	.features-content {
		max-width: 1100px;
		margin: 0 auto;
		text-align: center;
	}

	.features-content h2 {
		font-size: 2.4rem;
		margin-bottom: 20px;
	}

	.features-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
		gap: 24px;
	}

	.feature-card {
		display: flex;
		gap: 20px;
		border-radius: 12px;
		padding: 28px 24px;
		text-align: left;
		transition: box-shadow 0.2s, transform 0.2s;
	}

	.feature-card:hover {
		box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
		transform: translateY(-3px);
	}

	.feature-icon-wrap {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		border-radius: 10px;
		background: var(--hover);
		color: white;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.15rem;
	}

	.feature-text h3 {
		font-size: 1.05rem;
		margin-bottom: 8px;
		color: #222;
	}

	.feature-text p {
		font-size: 0.92rem;
		line-height: 1.65;
		color: #666;
	}

	/* ─── How it works ────────────────────────────────── */
	.how-section {
		padding: 90px 20px;
	}

	.how-content {
		max-width: 960px;
		margin: 0 auto;
		text-align: center;
	}

	.how-content h2 {
		font-size: 2.4rem;
		margin-bottom: 60px;
	}

	.steps {
		display: flex;
		align-items: flex-start;
		justify-content: center;
		gap: 10px;
		flex-wrap: wrap;
	}

	.step {
		flex: 1;
		min-width: 220px;
		max-width: 280px;
		text-align: center;
	}

	.step-number {
		width: 60px;
		height: 60px;
		border-radius: 50%;
		background: var(--hover);
		color: white;
		font-size: 1.5rem;
		font-weight: 700;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 20px;
	}

	.step h3 {
		font-size: 1.1rem;
		margin-bottom: 10px;
	}

	.step p {
		font-size: 0.95rem;
		line-height: 1.65;
		color: #666;
	}

	.step-arrow {
		font-size: 1.4rem;
		color: var(--hover);
		margin-top: 20px;
		padding: 0 5px;
	}

	/* ─── Metrics section ─────────────────────────────── */
	.metrics-section {
		padding: 90px 20px;
	}

	.metrics-content {
		max-width: 1100px;
		margin: 0 auto;
		text-align: center;
	}

	.metrics-content h2 {
		font-size: 2.4rem;
		margin-bottom: 20px;
	}

	.metrics-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
		gap: 24px;
		text-align: left;
	}

	.metric-group {
		border-radius: 12px;
		padding: 28px 24px;
	}

	.metric-group-title {
		font-weight: 700;
		font-size: 1rem;
		color: #222;
		margin-bottom: 16px;
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.metric-group-title i {
		color: var(--hover);
	}

	.metric-group ul {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.metric-group ul li {
		font-size: 0.93rem;
		color: #555;
		padding: 6px 0;
		border-bottom: 1px solid #e9ecef;
		line-height: 1.5;
	}

	.metric-group ul li:last-child {
		border-bottom: none;
	}

	.metric-group ul li::before {
		content: "✓ ";
		color: var(--hover);
		font-weight: 600;
	}

	/* ─── Use cases ────────────────────────────────────── */
	.usecases-section {
		padding: 90px 20px;
	}

	.usecases-content {
		max-width: 1000px;
		margin: 0 auto;
		text-align: center;
	}

	.usecases-content h2 {
		font-size: 2.4rem;
		margin-bottom: 50px;
	}

	.usecases {
		display: flex;
		gap: 30px;
		flex-wrap: wrap;
		justify-content: center;
	}

	.usecase {
		flex: 1;
		min-width: 240px;
		max-width: 290px;
		background: white;
		border-radius: 12px;
		padding: 36px 28px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
	}

	.usecase-icon {
		width: 60px;
		height: 60px;
		border-radius: 50%;
		background: #fff0ed;
		color: var(--hover);
		font-size: 1.5rem;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 18px;
	}

	.usecase h3 {
		font-size: 1.1rem;
		margin-bottom: 10px;
		color: #222;
	}

	.usecase p {
		font-size: 0.93rem;
		line-height: 1.65;
		color: #666;
	}

	/* ─── CTA section ─────────────────────────────────── */
	.cta-section {
		padding: 100px 20px;
		text-align: center;
	}

	.cta-content {
		max-width: 680px;
		margin: 0 auto;
	}

	.cta-content h2 {
		font-size: 2.4rem;
		margin-bottom: 20px;
	}

	.cta-content > p {
		font-size: 1.1rem;
		line-height: 1.7;
		color: #555;
		margin-bottom: 36px;
	}

	.cta-note {
		font-size: 0.88rem;
		color: #888;
		margin-top: 10px;
	}

	/* ─── Data quality section ───────────────────────── */
	.quality-section {
		padding: 90px 20px;
	}

	.quality-content {
		max-width: 1100px;
		margin: 0 auto;
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 70px;
		align-items: start;
	}

	.quality-badge {
		display: inline-block;
		background: #fff0ed;
		color: var(--hover);
		border: 1px solid rgba(236, 119, 106, 0.4);
		padding: 5px 14px;
		border-radius: 20px;
		font-size: 0.8rem;
		font-weight: 700;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		margin-bottom: 18px;
	}

	.quality-text h2 {
		font-size: 2rem;
		margin-bottom: 18px;
		line-height: 1.25;
	}

	.quality-text p {
		font-size: 0.97rem;
		line-height: 1.75;
		color: #555;
		margin-bottom: 16px;
	}

	.quality-caveat {
		display: flex;
		gap: 12px;
		align-items: flex-start;
		background: #fff8e6;
		border: 1px solid #f0d080;
		border-radius: 8px;
		padding: 14px 16px;
		margin-top: 20px;
		font-size: 0.88rem;
		color: #6b5000;
		line-height: 1.6;
	}

	.quality-caveat i {
		flex-shrink: 0;
		margin-top: 2px;
		color: var(--hover);
	}

	.quality-points {
		display: flex;
		flex-direction: column;
		gap: 22px;
	}

	.quality-point {
		display: flex;
		gap: 16px;
		align-items: flex-start;
	}

	.qp-icon {
		flex-shrink: 0;
		width: 44px;
		height: 44px;
		border-radius: 10px;
		background: white;
		border: 1px solid rgba(236, 119, 106, 0.35);
		color: var(--hover);
		font-size: 1.05rem;
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
	}

	.quality-point h4 {
		font-size: 0.97rem;
		margin: 0 0 5px;
		color: #222;
	}

	.quality-point p {
		font-size: 0.87rem;
		line-height: 1.6;
		color: #666;
		margin: 0;
	}

	/* ─── Pricing section ─────────────────────────────── */
	.pricing-section {
		padding: 90px 20px;
	}

	.pricing-content {
		max-width: 700px;
		margin: 0 auto;
		text-align: center;
	}

	.pricing-content h2 {
		font-size: 2.2rem;
		margin-bottom: 16px;
	}

	.pricing-card {
		margin-top: 10px;
		border-radius: 16px;
		overflow: hidden;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
		border: 1px solid #e9ecef;
	}

	.pricing-card-inner {
		background: #fff;
		padding: 44px 40px 36px;
	}

	.pricing-label {
		font-size: 0.8rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--hover);
		margin-bottom: 10px;
	}

	.pricing-price {
		font-size: 1.8rem;
		font-weight: 700;
		color: #222;
		margin-bottom: 14px;
	}

	.pricing-tagline {
		font-size: 0.95rem;
		color: #666;
		line-height: 1.65;
		margin-bottom: 28px;
	}

	.pricing-list {
		list-style: none;
		padding: 0;
		margin: 0 0 32px;
		text-align: left;
	}

	.pricing-list li {
		display: flex;
		align-items: center;
		gap: 10px;
		font-size: 0.95rem;
		color: #444;
		padding: 9px 0;
		border-bottom: 1px solid #e9ecef;
	}

	.pricing-list li:last-child {
		border-bottom: none;
	}

	.pricing-list li i {
		color: var(--hover);
		font-size: 0.9rem;
		flex-shrink: 0;
	}

	.pricing-cta {
		width: 100%;
		padding: 15px;
		font-size: 1rem;
		background-color: var(--hover);
	}
	.pricing-cta:hover {
		background-color: #e85a4a;
	}

	.pricing-note {
		background: #1c2128;
		color: rgba(255, 255, 255, 0.8);
		padding: 20px 28px;
		font-size: 0.9rem;
		line-height: 1.65;
		display: flex;
		gap: 12px;
		align-items: flex-start;
		text-align: left;
	}

	.pricing-note i {
		color: var(--hover);
		flex-shrink: 0;
		margin-top: 2px;
	}

	/* ─── Responsive ──────────────────────────────────── */
	@media (max-width: 768px) {
		.hero-content h1 {
			font-size: 2.1rem;
		}

		.step-arrow {
			display: none;
		}

		.steps {
			flex-direction: column;
			align-items: center;
		}

		.features-grid {
			grid-template-columns: 1fr;
		}

		.metrics-grid {
			grid-template-columns: 1fr;
		}

		.problem-cards,
		.usecases {
			flex-direction: column;
			align-items: center;
		}

		.problem-card,
		.usecase {
			max-width: 100%;
		}

		.quality-content {
			grid-template-columns: 1fr;
			gap: 40px;
		}

		.pricing-card-inner {
			padding: 30px 24px;
		}
	}
</style>
